import BigNumber from "bignumber.js";

/**
 * Defines a customization of a product
 * .......... MORE INFORMATION LATER ....................
 */
export class Customization {
    static readonly ID_PREFIX = "custom";

    title: string;
    description: string;
    displayOrder: number;
    active: boolean;

    rules: CustomizationRules;
    customizationItems: Map<string, CustomizationItems>;

    priceForEach: number;
    deliveryPriceForEach: number|null;
    defaultPriceRules: Map<string, number>;
    deliveryPriceRules: Map<string, number>;
    // priceForEachWithCondition: Map<string, number>;
    // deliveryPriceForEachWithCondition: Map<string, number>;

    receiptRules:ReceiptRules|null

    dependeeItems:DependeeItems[]
    dependsOn:string|null;

    /**
     * Constructor
     * @param title 
     * @param description 
     * @param displayOrder 
     * @param active 
     * @param rules 
     * @param customizationItems 
     * @param priceForEach 
     * @param deliveryPriceForEach 
     * @param defaultPriceRules 
     * @param deliveryPriceRules
     * @param receiptRules
     * @param dependeeItems Using this, you can define a "title" for a group "customizationItems". These titles can then be used to show/hide other customizations, using the dependsOnField. Read the "dependsOn" below for mor info.
     * @param dependsOn Say you have a BURGER item. This burger has a "SIDES" customization with the customization items: "fries" (id = "option1") or "salad" (id = "option2"). 
     * You also have "salad dressing", that should only be displayed if "salad" is selected.
     * To do that, you would set the dependeeItems for the SIDES customization with {title: "saladIsSelected", customizationItemIds:["option2"]}.
     * And then, on the "salad dressing" you would set "dependsOn: 'saladIsSelected'".
     * That way, the salad dressing would only show up if SALAD is selected.
     */
    constructor(title: string, description: string, displayOrder: number, active: boolean, rules: CustomizationRules,
        customizationItems: Map<string, CustomizationItems>, priceForEach: number, deliveryPriceForEach: number|null, 
        defaultPriceRules: Map<string, number>, deliveryPriceRules: Map<string, number>,
        // priceForEachWithCondition: Map<string, number>, deliveryPriceForEachWithCondition:Map<string, number>, 
        receiptRules:ReceiptRules|null,
        dependeeItems:DependeeItems[]|null, dependsOn:string|null
    ) {
        this.title = title;
        this.description = description;
        this.displayOrder = displayOrder;
        this.active = active;
        this.rules = rules;

        this.customizationItems = customizationItems;
        this.priceForEach = priceForEach;
        this.deliveryPriceForEach = deliveryPriceForEach;
        this.defaultPriceRules = defaultPriceRules
        this.deliveryPriceRules = deliveryPriceRules;

        // this.priceForEachWithCondition = priceForEachWithCondition;
        // this.deliveryPriceForEachWithCondition = deliveryPriceForEachWithCondition;

        this.receiptRules = receiptRules;

        this.dependeeItems = dependeeItems!=null?dependeeItems:[];
        this.dependsOn = dependsOn;
    }

    hasDeliveryPrices(){
        return this.deliveryPriceForEach != null || this.deliveryPriceRules.size>0;
    }

    getSelectionInformation(selectedItems: { optionId: string, quantity: number, itemMultiplier:number, toppingSide:"left"|"full"|"right"|null }[]):{selectionString:string, price:number, deliveryPrice:number}{
        const calculatedPrice = this.calculateSelectionPrice(selectedItems,false);
        const calculatedDelPrice = this.hasDeliveryPrices()?this.calculateSelectionPrice(selectedItems,true):calculatedPrice;

        return {selectionString:this.getSelectionString(selectedItems), price:calculatedPrice, deliveryPrice:calculatedDelPrice}
    }

    getSelectionString(selectedItems: { optionId: string, quantity: number, itemMultiplier:number, toppingSide:"left"|"full"|"right"|null }[]):string{
        let result = "";
        
        selectedItems.forEach(element => {
            if(result!=""){
                result += ", "
            }

            let qttyStr = "";
            if(element.quantity>1){
                qttyStr = element.quantity+"x "
            }

            let sideStr = "";
            if(element.toppingSide == "left" || element.toppingSide=="right"){ 
                sideStr = element.toppingSide.toUpperCase()+ " - ";
            }

            result += qttyStr + sideStr + this.customizationItems.get(element.optionId)!.title;
        });

        return result;
    }
    
    calculateSelectionPrice(selectedItems: { optionId: string, quantity: number, itemMultiplier:number, toppingSide:"left"|"full"|"right"|null }[], isDelivery: boolean):number {
        let totalPrice = new BigNumber(0);
        selectedItems.forEach((item) => {

            let halfToppingMultiplier = 1;
            //IS HALF TOPPING
            if(item.toppingSide == "left" || item.toppingSide == "right"){
                halfToppingMultiplier = 0.5;
            }

            totalPrice = totalPrice.plus(
                new BigNumber(this.getPrice(item.optionId, isDelivery)).times(item.quantity).times(halfToppingMultiplier)
            );
        })

        if (this.rules.numberOfFreeItems > 0) {
            //If I have free options, I MUST have the priceForEach set
            // (it doest make sense to have free options if the options have different values)
            totalPrice = totalPrice.minus(
                new BigNumber(this.priceForEach).times(this.rules.numberOfFreeItems)
            )

            if (totalPrice.lt(0)) {
                totalPrice = new BigNumber(0);
            }
        }

        return totalPrice.decimalPlaces(2).toNumber();
    }

    /**
     * Checks if the selected items are valid or not
     * @param selectedItems 
     * @param isDelivery 
     */
    isValidSelection(selectedItems: { optionId: string, quantity: number, itemMultiplier:number, toppingSide:"left"|"full"|"right"|null }[]):boolean{
        if(this.rules!=null && selectedItems!=null){
            let totalQtty = 0;
            selectedItems.forEach((item) => { 
                let halfToppingMultiplier = 1;
                //IS HALF TOPPING
                if(item.toppingSide == "left" || item.toppingSide == "right"){
                    halfToppingMultiplier = 0.5;
                }
                totalQtty += item.quantity * item.itemMultiplier * halfToppingMultiplier; 
            })
            if(this.rules.min!=null && this.rules.min>0){
                if(totalQtty < this.rules.min){
                    return false;
                }
            }
            if(this.rules.max!=null){
                if(totalQtty > this.rules.max){
                    return false;
                }
            }
        }
        return true;
    }

    /** Gets the base price for a customization item */
    getPrice(itemId: string, isDelivery: boolean): number{
        let price = new BigNumber(0);
        if (isDelivery && this.hasDeliveryPrices()) {
            if(this.deliveryPriceForEach!=null){
                price = new BigNumber(this.deliveryPriceForEach);    
            }else if(this.deliveryPriceRules!=null && this.deliveryPriceRules.has(itemId)){
                price = new BigNumber(this.deliveryPriceRules.get(itemId)!);
            }
        } else {
            if(this.priceForEach!=null){
                price = new BigNumber(this.priceForEach);    
            }else if(this.defaultPriceRules!=null && this.defaultPriceRules.has(itemId)){
                price = new BigNumber(this.defaultPriceRules.get(itemId)!);
            }
        }

        const selectedItem = this.customizationItems.get(itemId);
        let priceMultiplier = 1;
        if(selectedItem!=null && selectedItem.countsAsMultiple!=null){
            priceMultiplier = selectedItem.countsAsMultiple;
        }
        return price.times(priceMultiplier).toNumber();
    }

    static newFromAWS(awsItem: any) {

        this.validateAWSObj(awsItem);

        return new Customization(
            awsItem.title,
            awsItem.description,
            awsItem.displayOrder,
            awsItem.active,
            CustomizationRules.newFromAWS(awsItem.rules),
            CustomizationItems.newFromAWS(awsItem.customizationItems),
            awsItem.priceForEach,
            awsItem.deliveryPriceForEach,
            Customization.newPriceRulesMap(awsItem.defaultPriceRules),
            Customization.newPriceRulesMap(awsItem.deliveryPriceRules),
            // awsItem.priceForEachWithCondition,
            // awsItem.deliveryPriceForEachWithCondition,
            ReceiptRules.newFromAWS(awsItem.receiptRules),
            DependeeItems.newFromAWS(awsItem.dependeeItems),
            awsItem.dependsOn
        );
    }

    private static newPriceRulesMap(awsPriceRulesItem:any): Map<string, number> {
        const map = new Map<string, number>();
        if (awsPriceRulesItem != null) {
            Object.keys(awsPriceRulesItem).forEach(key => {
                map.set(key, awsPriceRulesItem[key]);
            });
        }
        return map;
    }

    static validateAWSObj(awsItem: any){
        let errorMsg = "";
        if(awsItem.title==null){
            errorMsg += "TITLE WAS NOT SET - ";
        }
        if(awsItem.rules!=null && awsItem.rules.numberOfFreeItems!=null &&  awsItem.priceForEach==null){
            errorMsg += "Number of free items set, but no price for each item set! - ";
        }

        if(errorMsg!=""){
            console.error("Error on item retrieved from DB: ",awsItem)
            throw new Error("- "+errorMsg)
        }
    }
}

export class CustomizationRules {
    min: number;
    max: number;
    allowQttyEdit: boolean;
    defaultSelection: Map<string, number>//ids of the items selected by default and the quantity selected bu default;
    numberOfFreeItems: number;//Should only be set if all the prices are the same
    showPizzaHalfToppingOptions:boolean;

    /**
     * Constructor
     * @param min Minimum ammount of items that can be selected
     * @param max Maximum ammount of items that can be selected
     * @param allowQttyEdit Whether you can edit how many of these customizations you want or not
     * @param defaultSelection which items should be selected by default
     * @param numberOfFreeItems number of items that can be selected for free
     * @param showPizzaHalfToppingOptions if true, allows the customer to select which side of the pizza he wants the topping to be put on
     */
    constructor(min: number, max: number, allowQttyEdit: boolean, defaultSelection: Map<string, number>, numberOfFreeItems: number, showPizzaHalfToppingOptions:boolean) {
        this.min = (min != null) ? min : 0;
        this.max = max;
        this.allowQttyEdit = (allowQttyEdit != null) ? allowQttyEdit : false;
        this.defaultSelection = (defaultSelection != null) ? defaultSelection : new Map();
        this.numberOfFreeItems = (numberOfFreeItems != null) ? numberOfFreeItems : 0;
        this.showPizzaHalfToppingOptions = (showPizzaHalfToppingOptions != null) ? showPizzaHalfToppingOptions : false;
    }

    static newFromAWS(awsItem: any) {
        if(awsItem!=null){
            return new CustomizationRules(
                awsItem.min,
                awsItem.max,
                awsItem.allowQttyEdit,
                this.newDefaultSelectionMap(awsItem.defaultSelection),
                awsItem.numberOfFreeItems,
                awsItem.showPizzaHalfToppingOptions
            );
        }else{
            return {} as CustomizationRules;
        }
    }

    private static newDefaultSelectionMap(awsDefaultSelection:any): Map<string, number> {
        const map = new Map<string, number>();
        if (awsDefaultSelection != null) {
            Object.keys(awsDefaultSelection).forEach(key => {
                map.set(key, awsDefaultSelection[key]);
            });
        }
        return map;
    }
}

export class CustomizationItems {
    title: string;
    countsAsMultiple:number;
    displayOrder: number;

    /**
     * constructor
     * @param title Title of the product
     * @param displayOrder Order in which this item should be displayed. 0 is shown first, then 1, then 2....
     * @param countsAsMultiple If this is a pizza topping, for example, and this item is supposed to count as 2 items (double topping), you would set this as "2"
     */
    constructor(title: string, displayOrder: number, countsAsMultiple:number) {
        this.title = title;
        this.displayOrder = displayOrder;
        this.countsAsMultiple = countsAsMultiple;
    }

    static newFromAWS(awsCustomizationItems: any): Map<string, CustomizationItems> {
        const customizationItemsMap: Map<string, CustomizationItems> = new Map();

        if (awsCustomizationItems != null) {
            Object.keys(awsCustomizationItems).forEach(key => {
                const obj = awsCustomizationItems[key];
                customizationItemsMap.set(key, new CustomizationItems(obj.title, obj.displayOrder, obj.countsAsMultiple));
            });
        }
        return customizationItemsMap;
    }
}

export class ReceiptRules{
    showTitleOnReceipt:boolean|null
    prefixStringForItemOnReceipt:string|null
    suffixStringForItemOnReceipt:string|null
    hideFromReceipt:string|null
    group:{
        groupTitle:string, 
        addToThisGroupIfCustomizationItemTitleStartsWith:string
        hideCustomizationItemTitle:boolean,
    }[]|null


    
    /** CONSTRUCTOR
     * Adds configurations related to how the receipt should be displayed.
     * @param showTitleOnReceipt If this customization should have it's title displayed on the receipt
     * @param prefixStringForItemOnReceipt prefix on the receipt for the item. For example, if we use #, the receipt will show "#nameOfTheItem"
     * @param suffixStringForItemOnReceipt sufix on the receipt for the item. For example, if we use #, the receipt will show "nameOfTheItem#"
     * @param hideFromReceipt determines which customization items should NOT show on the receipt. Usually the one selected by default. Just put it's ID here.
     * @param group Customizations under the same group will be displayed together.
     * So for example, if you have one product called "two pizza special", where you can select 2 pizzas, and you have:
     *     pizza 1 - crust
     *     pizza 1 - sauce
     *     pizza 1 - toppings
     * and 
     *     pizza 2 - crust
     *     pizza 2 - sauce
     *     pizza 2 - toppings
     * You could for example put all pizza 1 customizations on the "PIZZA 1" group and all the pizza 2 customizations on the "PIZZA 2" group
     * That way, when you printed the receipt, it would organize all those customizations together
     * ---
     * - YOU SHOULD EITHER HAVE ONE ELEMENT ON THIS ARRAY WITH A groupTitle (MANDATORY)
     * - OR MULTIPLE GROUPS, WITH MULTIPLE GROUP TITLES BUT DIFFERENT "addToThisGroupIfCustomizationItemTitleStartsWith"
     * - AND GOD KNOWS WHAT WILL HAPPEN IF YOU MESS IT UP AND YOU HAVE AN ITEM THAT DOES NOT BELONG TO EITHER GROUP
     * ---
     * groupTitle = name of the group. Items with the same name will ber grouped together. 
     * 
     * addToThisGroupIfCustomizationItemTitleStartsWith = If the items on this customization can belong to different groups, you can use this variable to sort them.
     * If present, you should have multiple groups, one for each different "addToThisGroupIfCustomizationItemTitleStartsWith"
     * 
     * hideCustomizationItemTitle = If the customization title starts with this string, we will sort them into the group
     */
    constructor(
        showTitleOnReceipt:boolean|null, 
        prefixStringForItemOnReceipt:string|null, 
        suffixStringForItemOnReceipt:string|null,
        hideFromReceipt:string|null,
        group:{
            groupTitle:string, 
            addToThisGroupIfCustomizationItemTitleStartsWith:string
            hideCustomizationItemTitle:boolean,
        }[]|null
    ){
            this.showTitleOnReceipt = showTitleOnReceipt;
            this.prefixStringForItemOnReceipt = prefixStringForItemOnReceipt;
            this.suffixStringForItemOnReceipt = suffixStringForItemOnReceipt;
            this.hideFromReceipt = hideFromReceipt;
            this.group = group;
    }

    static newFromAWS(awsItem: any):ReceiptRules|null {
        if(awsItem!=null){
            return new ReceiptRules(
                awsItem.showTitleOnReceipt,
                awsItem.prefixStringForItemOnReceipt,
                awsItem.suffixStringForItemOnReceipt,
                awsItem.hideFromReceipt,
                awsItem.group
            );
        }else{
            return null;
        }
    }
}

export class DependeeItems{
    title:string
    customizationItemIds:string[]

    /**
     * 
     * @param title title of the dependee. All items who are dependant on this title will only be displayed if it is selected (IMPROVE DESCRIPTION)
     * @param customizationItemIds These items will ... when selected
     */
    constructor (title:string, customizationItemIds:string[]){
        this.title = title;
        this.customizationItemIds = customizationItemIds;
    }

    static newFromAWS(awsItem: any):DependeeItems[] {
        const dependeeItemList:DependeeItems[] = [];

        if(awsItem!=null){
            awsItem.forEach((item:{title:string,customizationItemIds:string[]}) => {
                dependeeItemList.push(new DependeeItems(
                        item.title,
                        item.customizationItemIds
                ));
            })
        }

        return dependeeItemList;
    }
}